import React, { PureComponent } from 'react'
import { func, shape } from 'prop-types'
import { withI18n } from '@active/react-utils/i18n'
import { success } from '@active/react-ui/FloatAlert'
import DefaultComponentLayout, { LayoutContent } from '../common/Layout'
import NewCreditCard from './components/NewCreditCard'
import EditCreditCard from './components/EditCreditCard'
import EditECheckEUDD from './components/EditECheckEUDD'
import PaymentMethodView from './components/PaymentMethodView'
import { countryCodeToCountry } from '../../shared/utils/commonUtils'
import NewECheckEUDD from './components/NewECheckEUDD'
import '../../shared/utils/ams-security-api-1.0.5'
import './components/wallet.components.scss'
import './Wallet.scss'

const defaultViewState = {
  isShowView: false,
  isShowEdit: false,
  isShowEditECheck: false,
  isShowNewCreditCard: false,
  isShowNewECheck: false,
}

const typeViewMappings = {
  CREDIT_CARD: 'isShowNewCreditCard',
  ECHECK: 'isShowNewECheck',
}
const createablePaymentMethods = Object.keys(typeViewMappings)

class Wallet extends PureComponent {
  static propTypes = {
    services: shape({
      getNewCreditCardConfig: func.isRequired,
      addCreditCard: func.isRequired,
      getWallet: func.isRequired,
      setPrimaryPaymentMethod: func.isRequired,
      deletePaymentMethod: func.isRequired,
      getPublicKey: func.isRequired,
      addEcheck: func.isRequired,
    }).isRequired,
    configureTrack: func.isRequired,
    login: func.isRequired,
    i18n: func.isRequired,
  }

  constructor(props) {
    super(props)
    this.state = {
      errMsg: null,
      paymentMethods: [],
      ...defaultViewState,
      // show view as default
      isShowView: true,
    }
  }

  componentDidMount() {
    const { configureTrack } = this.props
    configureTrack({
      eventName: 'Wallet',
      widget: 'wallet',
      pageType: 'wallet',
    })
    this.resetPaymentMethods()
  }

  setErrorMsg = (message, errorCode, resetError) => {
    if (resetError) {
      this.setState({ errMsg: null })
    } else {
      this.setState({
        errMsg: 'passport.error.server.default',
      })
      if (errorCode === 'INVALID_CREDENTIAL_LOGIN') {
        const { login } = this.props
        login()
      }
    }
  }

  resetPaymentMethods = () => {
    this.setState({ ...defaultViewState })
    const { services: { getWallet } } = this.props
    getWallet({
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
      resolve: result => {
        if (!result || result.status === 'FAILURE') {
          const { message, errorCode } = result || {}
          this.setErrorMsg(message, errorCode)
        } else {
          this.updateViewState({
            paymentMethods: countryCodeToCountry(result),
            isShowView: true,
            errMsg: null,
          })
        }
      },
      reject: ({ message, errorCode }) => {
        this.setErrorMsg(message, errorCode)
      },
    })
  }

  updateViewState = updated => {
    this.setState({ ...defaultViewState, ...updated })
    this.setErrorMsg(null, null, true)
  }

  showView = () => {
    this.updateViewState({ isShowView: true, errMsg: null })
  }

  showAdd = type => {
    this.updateViewState({ [typeViewMappings[type]]: true })
  }

  showEdit = selected => {
    if (selected.getJsonType() === 'CreditCardPaymentMethod') {
      this.updateViewState({ selected, isShowEdit: true })
    } else {
      this.updateViewState({ selected, isShowEditECheck: true })
    }
  }

  handleDelete = (selected, closePopup) => {
    const { services: { deletePaymentMethod } } = this.props

    deletePaymentMethod({
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
      params: { paymentMethodId: selected.id },
      resolve: () => {
        closePopup()
        this.resetPaymentMethods()
      },
      reject: ({ message, errorCode }) => {
        closePopup()
        this.setErrorMsg(message, errorCode)
      },
    })
  }

  handleChange = selected => {
    const { services: { setPrimaryPaymentMethod }, i18n } = this.props
    setPrimaryPaymentMethod({
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
      params: { paymentMethodId: selected.id },
      resolve: () => {
        success({
          message: i18n('passport.wallet.newCreditCard.success'),
          theme: 'wcag',
          dismissible: false,
          timeout: 3000,
        })
        this.resetPaymentMethods()
      },
      reject: ({ message, errorCode }) => {
        this.setErrorMsg(message, errorCode)
      },
    })
  }

  render() {
    const {
      services: {
        getNewCreditCardConfig,
        addCreditCard,
        updateCreditCard,
        getPublicKey,
        addEcheck,
        addEUDD,
        updateECheck,
        updateEUDD,
      },
    } = this.props
    const {
      paymentMethods, errMsg, selected,
      isShowNewCreditCard, isShowView, isShowEdit, isShowNewECheck, isShowEditECheck,
    } = this.state

    return (
      <DefaultComponentLayout nameSpace="passport__wallet" errMsg={errMsg}>
        <LayoutContent main>
          {
              isShowView
              && (
              <PaymentMethodView
                createablePaymentMethods={createablePaymentMethods}
                paymentMethods={paymentMethods}
                handleAddPaymentMethod={this.showAdd}
                handleEditPaymentMethod={this.showEdit}
                handleDeletePaymentMethod={this.handleDelete}
                handleChangePrimaryMethod={this.handleChange}
              />
              )
            }
          {
              isShowNewCreditCard
              && (
              <NewCreditCard
                getNewCreditCardConfig={getNewCreditCardConfig}
                addCreditCard={addCreditCard}
                setErrorMsg={this.setErrorMsg}
                handleBackView={this.showView}
                updatePaymentInfoList={this.resetPaymentMethods}
              />
              )
            }
          {
              isShowNewECheck
              && (
              <NewECheckEUDD
                addEcheck={addEcheck}
                addEUDD={addEUDD}
                getPublicKey={getPublicKey}
                setErrorMsg={this.setErrorMsg}
                handleBackView={this.showView}
                updatePaymentInfoList={this.resetPaymentMethods}
              />
              )
            }
          {
              isShowEdit
              && (
              <EditCreditCard
                paymentMethod={selected}
                updateCreditCard={updateCreditCard}
                setErrorMsg={this.setErrorMsg}
                handleBackView={this.showView}
                updatePaymentInfoList={this.resetPaymentMethods}
              />
              )
            }
          {
              isShowEditECheck
              && (
              <EditECheckEUDD
                paymentMethod={selected}
                updateECheck={updateECheck}
                updateEUDD={updateEUDD}
                setErrorMsg={this.setErrorMsg}
                handleBackView={this.showView}
                updatePaymentInfoList={this.resetPaymentMethods}
              />
              )
            }
        </LayoutContent>
      </DefaultComponentLayout>
    )
  }
}

export default withI18n(Wallet)
